import React, { useEffect, useMemo, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import './ManagerContractMobile.css';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Components/Sidebar/Sidebar';
import DashboardNavbar from './Components/Navbar/DashboardNavbar';
import Header from './Components/Header/BlueHeader';
import Pills from './Components/Pills/Pills';
import SearchBar from './Components/SearchBar/SearchBar';
import IssueMultiSelect from './Components/IssueMultiSelect/IssueMultiSelect';
import StatusMultiSelect from './Components/StatusMultiSelect/StatusMultiSelect';
import HarbourProjectsTable from './Components/Projects/HarbourProjectsTable/HarbourProjectsTable';
import Footer from './Components/Footer/Footer';
import API_BASE_URL from './config/api';
import { Spinner, Alert, Modal, Carousel } from 'react-bootstrap';

const PROJECT_MAINTENANCE_ID = '8888-1';

// Issue filter values keep the legacy labels while rows carry display names.
const ISSUE_FILTER_ALIASES = {
  Mould: ['Mould', 'Potential Mould'],
};

const determineInspectionStatus = (row) => {
  if (row.unitStatus === 'missed' || row.unitStatus === 'blocked') return 'Needs Service';
  if (row.valvesHeld === false) return 'Needs Service';
  if (row.actuatorReplaced !== true) return 'Needs Service';
  if (row.mouldPresent === true) return 'Needs Service';
  return 'OK';
};

const buildNotes = (row) => {
  const parts = [
    `Valves Held: ${row.valvesHoldStatus || 'Unknown'}`,
    `Actuator & Valve Replaced: ${row.actuatorReplacementStatus || 'Pending'}`,
    `Potential Mould: ${row.mouldStatus || 'No'}`,
  ];
  if (row.valveOutcome) parts.push(row.valveOutcome);
  if (row.replacementOutcome) parts.push(row.replacementOutcome);
  if (row.additionalNotes) parts.push(row.additionalNotes);
  if (row.mouldSummary && row.mouldSummary !== row.additionalNotes) parts.push(row.mouldSummary);
  if (row.missedUnitReason) parts.push(`Missed: ${row.missedUnitReason}`);
  return parts.filter(Boolean).join(' • ');
};

const coercePhotoList = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string' && value.startsWith('http')) return [value];
  return [];
};

const buildPhotoData = (row) => {
  const photos = [];
  const photoLabelMap = {};
  const badValvesPhotos = [];
  const actuatorBeforePhotos = [];
  const actuatorAfterPhotos = [];
  const mouldPhotos = [];

  const addPhotos = (list, label, type = 'issue') => {
    if (!list || !list.length) return;
    list.forEach((url) => {
      if (!url) return;
      if (!photoLabelMap[url]) {
        photos.push(url);
      }
      photoLabelMap[url] = { label, type };
    });
  };

  if (row.valvesHeld === false) {
    const badList = row.badValvesPhotos || row.bad_valves_photos || [];
    badValvesPhotos.push(...badList);
    addPhotos(badList, 'Bad Valves', 'issue');
  }

  const panelPhotosRaw = coercePhotoList(row.panelInteriorPhotos);
  const panelPhotos = panelPhotosRaw.length
    ? panelPhotosRaw
    : coercePhotoList(row.panel_interior_photos);
  const newActuatorPhotosRaw = coercePhotoList(row.newActuatorPhotos);
  const newActuatorPhotos = newActuatorPhotosRaw.length
    ? newActuatorPhotosRaw
    : coercePhotoList(row.new_actuator_photos);

  if (panelPhotos.length) {
    actuatorBeforePhotos.push(...panelPhotos);
    addPhotos(panelPhotos, 'Actuator - Before Replacement', 'before');
  }
  if (newActuatorPhotos.length) {
    actuatorAfterPhotos.push(...newActuatorPhotos);
    addPhotos(newActuatorPhotos, 'Actuator - After Replacement', 'after');
  }

  if (row.mouldPresent === true) {
    const mouldSet = row.mouldPhotos || row.mould_photos || [];
    mouldPhotos.push(...mouldSet);
    addPhotos(mouldSet, 'Potential Mould', 'issue');
  }

  addPhotos(row.issuePhotos || row.issue_photos, 'Project Photo', 'issue');

  return { photos, photoLabelMap, badValvesPhotos, actuatorBeforePhotos, actuatorAfterPhotos, mouldPhotos };
};

const mapHarbourRow = (row) => {
  const {
    photos,
    photoLabelMap,
    badValvesPhotos,
    actuatorBeforePhotos,
    actuatorAfterPhotos,
    mouldPhotos,
  } = buildPhotoData(row);
  const issues = [];
  if (row.valvesHeld === false) issues.push('Valves');
  if (row.actuatorReplaced !== true) issues.push('Actuator');
  if (row.mouldPresent === true) issues.push('Potential Mould');

  return {
    ...row,
    unit: row.unit || '',
    equipmentLocation: row.location || '',
    inspectionDate: row.visitDate || '',
    inspection: determineInspectionStatus(row),
    notes: buildNotes(row),
    issues,
    photos,
    badValvesPhotos,
    actuatorBeforePhotos,
    actuatorAfterPhotos,
    harbourMouldPhotos: mouldPhotos,
    photoLabelMap,
  };
};

const mapHarbourRows = (rows = []) => rows.map((row) => mapHarbourRow(row));

function Projects() {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [activeKey, setActiveKey] = useState('projects');
  const [buildingName, setBuildingName] = useState('');
  const [buildingManagerName, setBuildingManagerName] = useState();
  const [buildingPhotoUrl, setBuildingPhotoUrl] = useState('');
  const [hasProjectContract, setHasProjectContract] = useState(false);
  const [hasValveAuditContract, setHasValveAuditContract] = useState(false);
  const [maintenanceCycles, setMaintenanceCycles] = useState([]);
  const [selectedCycle, setSelectedCycle] = useState('');
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [isBuildingLoading, setIsBuildingLoading] = useState(true);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [stats, setStats] = useState({ totalUnits: 0, noIssues: 0, foundIssues: 0 });
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [issueFilters, setIssueFilters] = useState([]);
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [sortColumn, setSortColumn] = useState('unit');
  const [sortDirection, setSortDirection] = useState('asc');
  const [carouselPhotos, setCarouselPhotos] = useState([]);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [photoMetaMap, setPhotoMetaMap] = useState({});

  const token = localStorage.getItem('managerToken');
  const userStr = localStorage.getItem('managerUser') || '';
  let buildingId = '';

  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      buildingId = user.buildingId || '';
    } catch (err) {
      console.error('Error parsing user data:', err);
    }
  }

  const fetchBuildingDetails = async () => {
    if (!buildingId || !token) {
      setIsBuildingLoading(false);
      return;
    }
    try {
      const resp = await fetch(`${API_BASE_URL}/api/getBuildingById`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ buildingId }),
      });
      if (!resp.ok) {
        throw new Error(`Error fetching building: ${resp.status}`);
      }
      const data = await resp.json();
      setBuildingName(data.name || 'Unknown');
      setBuildingManagerName(Object.prototype.hasOwnProperty.call(data, 'manager') ? data.manager || '' : data.managerProfile?.manager || '');
      setBuildingPhotoUrl(data.photo_url || '');
      const contracts = data.contracts || [];
      setHasProjectContract(contracts.includes('project'));
      setHasValveAuditContract(contracts.includes('valve-audit'));
    } catch (error) {
      console.error('Failed to load building details:', error);
    } finally {
      setIsBuildingLoading(false);
    }
  };

  const fetchProjectData = async (maintenanceOverride = null) => {
    if (!token || !buildingId) return;
    const targetMaintenance = maintenanceOverride || selectedMaintenance;
    const maintenanceId = targetMaintenance?.maintenanceID || targetMaintenance?.objectId || PROJECT_MAINTENANCE_ID;
    if (!maintenanceId) return;
    setIsDataLoading(true);
    try {
      const dashRes = await fetch(`${API_BASE_URL}/api/getDashboardData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          maintenanceId,
          dataFormatVersion: 'harbour_actuator',
        }),
      });
      if (!dashRes.ok) {
        throw new Error(`Dashboard fetch error: ${dashRes.status}`);
      }
      const dashData = await dashRes.json();
      setStats(dashData.stats || { totalUnits: 0, noIssues: 0, foundIssues: 0 });

      const tableRes = await fetch(`${API_BASE_URL}/api/getTableData`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          maintenanceId,
          limit: 1000,
          skip: 0,
          dataFormatVersion: 'harbour_actuator',
        }),
      });
      if (!tableRes.ok) {
        throw new Error(`Table fetch error: ${tableRes.status}`);
      }
      const tableData = await tableRes.json();
      const mappedRows = mapHarbourRows(tableData.unitsData || []);
      setRows(mappedRows);
    } catch (error) {
      console.error('Failed to load project data:', error);
    } finally {
      setIsDataLoading(false);
    }
  };

  const fetchMaintenanceCycles = async () => {
    if (!token || !buildingId) return;
    try {
      const resp = await fetch(`${API_BASE_URL}/api/getMaintenanceInspections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ buildingId }),
      });

      if (!resp.ok) {
        throw new Error(`Maintenance inspections fetch error: ${resp.status}`);
      }

      const data = await resp.json();
      const cycles = (data.maintenanceInspections || [])
        .filter((cycle) => {
          const maintenanceId = cycle.maintenanceID || cycle.objectId;
          return (
            String(cycle.buildingId) === String(buildingId) &&
            (String(maintenanceId) === PROJECT_MAINTENANCE_ID || cycle.dataFormatVersion === 'harbour_actuator')
          );
        })
        .map((cycle) => ({
          ...cycle,
          maintenanceID: cycle.maintenanceID || cycle.objectId,
          cycle: cycle.cycle || cycle.cycleName || cycle.name || 'Projects Cycle',
        }))
        .sort((a, b) => {
          const dateA = new Date(a.createdAt || a.created_at || 0);
          const dateB = new Date(b.createdAt || b.created_at || 0);
          return dateB - dateA;
        });

      setMaintenanceCycles(cycles);
      if (cycles.length > 0) {
        setSelectedCycle(cycles[0].cycle || '');
        setSelectedMaintenance(cycles[0]);
      } else {
        setSelectedCycle('');
        setSelectedMaintenance(null);
      }
    } catch (error) {
      console.error('Failed to load project cycles:', error);
      setMaintenanceCycles([]);
      setSelectedCycle('');
      setSelectedMaintenance(null);
    }
  };

  useEffect(() => {
    fetchBuildingDetails();
  }, [buildingId, token]);

  useEffect(() => {
    fetchMaintenanceCycles();
  }, [buildingId, token]);

  useEffect(() => {
    if (!selectedCycle || maintenanceCycles.length === 0) return;
    const match = maintenanceCycles.find((cycle) => cycle.cycle === selectedCycle);
    if (match && match.maintenanceID !== selectedMaintenance?.maintenanceID) {
      setSelectedMaintenance(match);
    }
  }, [selectedCycle, maintenanceCycles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasProjectContract || !selectedMaintenance) return;
    fetchProjectData(selectedMaintenance);
  }, [hasProjectContract, selectedMaintenance]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleNavigate = (key) => {
    setActiveKey(key);
    switch (key) {
      case 'hvac':
        navigate('/');
        break;
      case 'mech':
        navigate('/mechanical-room');
        break;
      case 'dryerVents':
        navigate('/dryer-vents');
        break;
      case 'stackCleaning':
        navigate('/stack-cleaning');
        break;
      case 'waterSavings':
        navigate('/water-savings');
        break;
      case 'projects':
        navigate('/projects');
        break;
      case 'valveAudit':
        navigate('/valve-audit');
        break;
      default:
        break;
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row) => {
      const unitMatch = row.unit?.toLowerCase().includes(searchTerm.toLowerCase());
      const notesMatch = row.notes?.toLowerCase().includes(searchTerm.toLowerCase());
      const locationMatch = row.equipmentLocation?.toLowerCase().includes(searchTerm.toLowerCase());
      const searchMatch = unitMatch || notesMatch || locationMatch;

      let matchesIssues = true;
      if (issueFilters.length > 0) {
        matchesIssues = issueFilters.some((filter) =>
          (ISSUE_FILTER_ALIASES[filter] || [filter]).some((alias) => row.issues?.includes(alias))
        );
      }

      let matchesStatus = true;
      if (statusFilter === 'Needs Service') {
        matchesStatus = row.inspection !== 'OK';
      } else if (statusFilter === 'OK') {
        matchesStatus = row.inspection === 'OK';
      }

      return searchMatch && matchesIssues && matchesStatus;
    });
  }, [rows, searchTerm, issueFilters, statusFilter]);

  const sortedRows = useMemo(() => {
    const copy = [...filteredRows];
    copy.sort((a, b) => {
      let valA = a[sortColumn];
      let valB = b[sortColumn];

      if (sortColumn === 'inspectionDate') {
        valA = valA ? new Date(valA).getTime() : 0;
        valB = valB ? new Date(valB).getTime() : 0;
      } else if (sortColumn === 'actuatorReplaced') {
        const normalize = (val) => {
          if (val === true) return 2;
          if (val === false) return 1;
          return 0;
        };
        valA = normalize(a.actuatorReplaced);
        valB = normalize(b.actuatorReplaced);
      }

      if (valA === undefined || valA === null) valA = '';
      if (valB === undefined || valB === null) valB = '';

      if (typeof valA === 'string') valA = valA.toLowerCase();
      if (typeof valB === 'string') valB = valB.toLowerCase();

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [filteredRows, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedRows.length / rowsPerPage) || 1;
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageRows = sortedRows.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, searchTerm, issueFilters, statusFilter]);

  const handleSort = (columnKey) => {
    setCurrentPage(1);
    if (sortColumn === columnKey) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  const handleOpenCarousel = (photos = [], startIdx = 0, metaMap = {}) => {
    setCarouselPhotos(photos);
    setCarouselIndex(startIdx);
    setPhotoMetaMap(metaMap || {});
    setShowModal(true);
  };

  const renderGalleryModal = () => (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
      <Modal.Body className="p-0" style={{ position: 'relative' }}>
        <Carousel activeIndex={carouselIndex} onSelect={(idx) => setCarouselIndex(idx)} interval={null}>
          {(carouselPhotos || []).map((url, idx) => {
            const meta = photoMetaMap[url] || {};
            const isVideo = typeof url === 'string' && url.match(/\.(mp4|mov|webm|ogg|m4v)$/i);
            let banner = '';
            if (meta.customLabel) {
              banner = meta.customLabel;
            } else if (meta.type === 'before') {
              banner = 'Before';
            } else if (meta.type === 'after') {
              banner = 'After';
            } else if (meta.label) {
              banner = meta.label;
            }
            return (
              <Carousel.Item key={url || idx}>
                <div style={{ position: 'relative' }}>
                  {banner && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        backgroundColor: 'rgba(15,23,42,0.85)',
                        color: '#fff',
                        padding: '6px 12px',
                        borderRadius: 6,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        zIndex: 5,
                      }}
                    >
                      {banner}
                    </div>
                  )}
                  {isVideo ? (
                    <video src={url} controls className="d-block w-100" style={{ maxHeight: '80vh' }} />
                  ) : (
                    <img src={url} alt={banner || 'Preview'} className="d-block w-100" style={{ maxHeight: '80vh', objectFit: 'contain' }} />
                  )}
                </div>
              </Carousel.Item>
            );
          })}
        </Carousel>
      </Modal.Body>
    </Modal>
  );

  const projectsUnavailable = !isBuildingLoading && !hasProjectContract;

  return (
    <div style={{ display: 'flex' }}>
      <div
        className="dashboard-sidebar-host"
        onMouseEnter={() => setSidebarCollapsed(false)}
        onMouseLeave={() => setSidebarCollapsed(true)}
        style={{ height: '100vh' }}
      >
        <Sidebar
          managerName={buildingManagerName}
          activeKey={activeKey}
          onNavigate={handleNavigate}
          collapsed={sidebarCollapsed}
          onToggle={handleSidebarToggle}
          buildingName={buildingName}
          buildingPhotoUrl={buildingPhotoUrl}
          showProjectsTab={hasProjectContract}
          showValveAuditTab={hasValveAuditContract}
        />
      </div>
      <div
        className="dashboard-content manager-contract-page"
        style={{
          flex: 1,
          marginLeft: sidebarCollapsed ? '72px' : '240px',
          transition: 'margin-left 240ms',
          minHeight: '100vh',
          backgroundColor: '#f8f9fb',
        }}
      >
        <DashboardNavbar
          logoutInSidebar
          selectedMaintenance={selectedMaintenance || { maintenanceID: PROJECT_MAINTENANCE_ID }}
          buildingName={buildingName}
          buildingId={buildingId}
          contractLabel="Projects"
        />

        <Header
          maintenanceCycles={maintenanceCycles}
          selectedCycle={selectedCycle}
          setSelectedCycle={setSelectedCycle}
          selectedMaintenance={selectedMaintenance}
          contractLabel="Projects"
        />

        <div style={{ width: '100%', margin: '1rem auto', paddingBottom: '1.5rem' }}>
          {projectsUnavailable ? (
            <Alert variant="warning">
              Projects access isn’t enabled for this building. Please contact Hudson if you believe this is an error.
            </Alert>
          ) : isDataLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
              <Spinner animation="border" role="status" style={{ color: '#224982' }}>
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '1.5rem' }}>
                <Pills stats={stats} hvacType="fancoil" />
              </div>

              <div
                className="manager-contract-filters"
                style={{
                  width: '90%',
                  margin: '1rem auto',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'nowrap',
                }}
              >
                <div style={{ flex: 4 }}>
                  <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>
                <div style={{ flex: 2 }}>
                  <IssueMultiSelect issueFilters={issueFilters} setIssueFilters={setIssueFilters} />
                </div>
                <div style={{ flex: 2 }}>
                  <StatusMultiSelect statusFilter={statusFilter} setStatusFilter={setStatusFilter} />
                </div>
              </div>

              <HarbourProjectsTable
                rows={pageRows}
                currentPage={currentPage}
                totalPages={totalPages}
                totalRows={sortedRows.length}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(value) => setRowsPerPage(Number(value))}
                onPrevPage={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                onNextPage={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                startIndex={startIndex}
                endIndex={Math.min(endIndex, sortedRows.length)}
                onOpenCarousel={handleOpenCarousel}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
              />
            </>
          )}
        </div>

        <Footer />
        {renderGalleryModal()}
      </div>
    </div>
  );
}

export default Projects;

